import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Avatar,
  TextField,
  Button,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import PublicIcon from "@mui/icons-material/Public";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import HistoryIcon from "@mui/icons-material/History";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

const RequestDetail = ({
  request,
  onBack,
  onOpenApprove,
  onOpenReject,
  onOpenHistory,
  onOpenAttachments,
  onSelectAttachment,
  comment,
  setComment,
  onSwitchView,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!request) return null;

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "540px",
        margin: "0 auto",
        minHeight: "100vh",
        backgroundColor: "#F8FAFC",
        display: "flex",
        flexDirection: "column",
        pb: 3,
      }}
    >
      {/* Blue Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "linear-gradient(180deg, #0056C6 0%, #004AAD 100%)",
          color: "#ffffff",
          px: 2,
          pt: 1.5,
          pb: 2.5,
          borderBottomLeftRadius: 20,
          borderBottomRightRadius: 20,
          boxShadow: "0 4px 16px rgba(0, 74, 173, 0.2)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <IconButton
            onClick={onBack}
            sx={{
              color: "#ffffff",
              p: 0.8,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 24 }} />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              letterSpacing: "-0.01em",
              textAlign: "center",
              flex: 1,
            }}
          >
            Travel Request Details
          </Typography>

          <IconButton
            onClick={handleMenuClick}
            sx={{
              color: "#ffffff",
              p: 0.8,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 24 }} />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { borderRadius: "14px", minWidth: "180px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" },
            }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onOpenHistory();
              }}
              sx={{ gap: 1.2, fontSize: "0.9rem" }}
            >
              <HistoryIcon sx={{ fontSize: 20, color: "#0052CC" }} />
              Approval History
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onOpenAttachments();
              }}
              sx={{ gap: 1.2, fontSize: "0.9rem" }}
            >
              <AttachFileIcon sx={{ fontSize: 20, color: "#64748B" }} />
              All Attachments
            </MenuItem>
            {onSwitchView && (
              <>
                <Divider />
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    onSwitchView();
                  }}
                  sx={{ gap: 1.2, fontSize: "0.9rem" }}
                >
                  <SwapHorizIcon sx={{ fontSize: 20, color: "#0052CC" }} />
                  Switch to Request Details 2
                </MenuItem>
              </>
            )}
          </Menu>
        </Box>
      </Box>

      {/* Content Container */}
      <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Profile Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "18px",
            backgroundColor: "#ffffff",
            border: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <Avatar
            src={request.avatar}
            alt={request.name}
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "2px solid #ffffff",
            }}
          />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", color: "#1E293B", lineHeight: 1.3 }}>
              {request.name}
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#64748B", mt: 0.3 }}>
              Employee No : <Box component="span" sx={{ fontWeight: 600, color: "#475569" }}>{request.employeeNo}</Box>
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#64748B" }}>
              {request.role}
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#64748B" }}>
              {request.department}
            </Typography>
          </Box>
        </Paper>

        {/* Travel Information Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "18px",
            backgroundColor: "#ffffff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
            <FlightTakeoffIcon sx={{ fontSize: 22, color: "#0052CC" }} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.98rem", color: "#1E293B" }}>
              Travel Information
            </Typography>
          </Box>

          {/* Details list */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.6 }}>
            {/* Country */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748B" }}>
                <PublicIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: "0.88rem", color: "#64748B" }}>Country</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#1E293B" }}>
                {request.country}
              </Typography>
            </Box>

            {/* Departure Date */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748B" }}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: "0.88rem", color: "#64748B" }}>Departure Date</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#1E293B" }}>
                {request.departureDate}
              </Typography>
            </Box>

            {/* Return Date */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748B" }}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: "0.88rem", color: "#64748B" }}>Return Date</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#1E293B" }}>
                {request.returnDate}
              </Typography>
            </Box>

            {/* Duration */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748B" }}>
                <AccessTimeIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: "0.88rem", color: "#64748B" }}>Duration</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#1E293B" }}>
                {request.duration}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Purpose Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "18px",
            backgroundColor: "#ffffff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 22, color: "#0052CC" }} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.98rem", color: "#1E293B" }}>
              Purpose
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.9rem", color: "#475569", lineHeight: 1.55 }}>
            {request.purpose}
          </Typography>
        </Paper>

        {/* Attachments Card */}
        {/* <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "18px",
            backgroundColor: "#ffffff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.8 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <AttachFileIcon sx={{ fontSize: 22, color: "#0052CC" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.98rem", color: "#1E293B" }}>
                Attachments ({request.attachments?.length || 0})
              </Typography>
            </Box>
            <Button
              variant="text"
              onClick={onOpenAttachments}
              sx={{
                color: "#0052CC",
                fontWeight: 600,
                fontSize: "0.85rem",
                textTransform: "none",
                p: 0,
                minWidth: "auto",
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              View All
            </Button>
          </Box>

           
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(3, 1fr)" },
              gap: 1.5,
            }}
          >
            {request.attachments?.map((file) => (
              <Box
                key={file.id}
                onClick={() => onSelectAttachment(file)}
                sx={{
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: "12px",
                  p: 1.2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  textAlign: "center",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#0052CC",
                    backgroundColor: "#EFF6FF",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {file.type === "pdf" ? (
                  <PictureAsPdfIcon sx={{ fontSize: 32, color: "#EF4444", mb: 0.8 }} />
                ) : (
                  <ImageIcon sx={{ fontSize: 32, color: "#2563EB", mb: 0.8 }} />
                )}
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#334155",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={file.name}
                >
                  {file.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper> */}

        {/* Comments Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "18px",
            backgroundColor: "#ffffff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 20, color: "#0052CC" }} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.98rem", color: "#1E293B" }}>
              Comments
            </Typography>
          </Box>

          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Enter approval comments..."
            value={comment}
            onChange={(e) => {
              if (e.target.value.length <= 500) {
                setComment(e.target.value);
              }
            }}
            sx={{
              backgroundColor: "#F8FAFC",
              borderRadius: "12px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": { borderColor: "#E2E8F0" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
                "&.Mui-focused fieldset": { borderColor: "#0052CC" },
              },
              "& .MuiInputBase-input": { fontSize: "0.9rem", color: "#1E293B" },
            }}
          />
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "#94A3B8",
              textAlign: "right",
              mt: 0.5,
            }}
          >
            {comment.length}/500
          </Typography>
        </Paper>

        {/* Action Buttons Floating Bar */}
        {request?.status?.toLowerCase() === "pending" && (
        <Box
          sx={{
            position: "sticky",
            bottom: 12,
            zIndex: 5,
            width: "100%",
            mt: 1,
          }}
        >
          <Paper
            elevation={4}
            sx={{
              width: "100%",
              p: 1.5,
              borderRadius: "18px",
              backgroundColor: "#ffffff",
              boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
              border: "1px solid #E2E8F0",
              display: "flex",
              gap: 1.5,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={onOpenReject}
              startIcon={<CloseIcon />}
              sx={{
                backgroundColor: "#EF4444",
                color: "#ffffff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                borderRadius: "12px",
                py: 1.2,
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.25)",
                "&:hover": { backgroundColor: "#DC2626" },
              }}
            >
              Reject
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={onOpenApprove}
              startIcon={<CheckIcon />}
              sx={{
                backgroundColor: "#16A34A",
                color: "#ffffff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                borderRadius: "12px",
                py: 1.2,
                boxShadow: "0 4px 12px rgba(22, 163, 74, 0.25)",
                "&:hover": { backgroundColor: "#15803D" },
              }}
            >
              Approve
            </Button>
          </Paper>
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default RequestDetail;
